import {
  BellNotifications,
  CheckCircle2,
  Flag,
  PenEdit,
  Tag,
  Target,
  Warning,
} from '@griply/assets';
import {
  ScheduleEntryState,
  Task,
  TaskType,
  Timestamp,
  UpdateTask,
} from '@griply/types';
import { isTimeslotAction } from '@griply/utils';
import { useTaskTodayControls } from 'features/tasks';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TaskDateQuickActions } from 'shared/components/connected/task-date-quick-actions';
import { TaskPriorityQuickActions } from 'shared/components/connected/task-priority-quick-actions';
import { TaskRescheduleQuickActions } from 'shared/components/connected/task-reschedule-quick-actions';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import {
  MetaViewOptions,
  useOpenTaskDetail,
} from 'shared/contexts/task-detail';
import { focusOnEndOfEditableContent } from 'shared/utils/focus-on-end-of-element';
import { getPlatformShortcut } from 'shared/utils/get-platform-shortcut';

import { SubMenu } from './types';

export type EditListProps = {
  task: Task;
  onClose: () => void;
  openSubMenu: (subMenu: SubMenu) => void;
  nameRef?: React.RefObject<HTMLDivElement>;
  onAction?: () => void;
  onUpdate?: (task: Task, updates: UpdateTask) => void;
  hideEdit?: boolean;
  completeActions?: React.ReactNode;
  occurrenceDate?: Timestamp;
};

export const EditList: React.FC<EditListProps> = ({
  task,
  onClose,
  openSubMenu,
  nameRef,
  onAction,
  onUpdate,
  hideEdit,
  completeActions,
  occurrenceDate,
}) => {
  const { t } = useTranslation();
  const openTaskDetail = useOpenTaskDetail();
  const { onComplete } = useTaskTodayControls();

  const isHabit = [TaskType.Habit, TaskType.Repeating].includes(task.type);
  const isFocusBlock = isTimeslotAction(task.action?.type);
  const quickActionTasks = useMemo(() => [task], [task]);

  const onEdit = () => {
    openTaskDetail(task, MetaViewOptions.Settings);

    if (nameRef?.current) {
      focusOnEndOfEditableContent(nameRef.current);
    }

    onAction?.();
    onClose();
  };

  const onCompleteFocusBlock = () => {
    onComplete(task, ScheduleEntryState.Complete, { keepDetailOpen: true });
    onAction?.();
    onClose();
  };

  const updatePriority = () => openSubMenu(SubMenu.Priority);
  const updateLinkedItem = () => openSubMenu(SubMenu.LinkedItem);
  const updateDeadlineDeadline = () => openSubMenu(SubMenu.DeadlineDeadline);
  const updateReminderTime = () => openSubMenu(SubMenu.ReminderTime);
  const selectDate = () => openSubMenu(SubMenu.SelectDate);
  const updateTags = () => openSubMenu(SubMenu.Tags);

  return (
    <>
      {!hideEdit && isHabit && (
        <PopupMenuList hasBottomBorder>
          <PopupMenuListItem>
            <PopupMenuButton onClick={onEdit} start={<Icon icon={PenEdit} />}>
              {t('context-menus.task.labels.edit')}
            </PopupMenuButton>
          </PopupMenuListItem>
        </PopupMenuList>
      )}

      {isFocusBlock && (
        <PopupMenuList hasBottomBorder>
          <PopupMenuListItem>
            <PopupMenuButton
              onClick={onCompleteFocusBlock}
              start={<Icon icon={CheckCircle2} />}
            >
              {t(
                task.completedAt
                  ? 'context-menus.task.labels.un-complete'
                  : 'context-menus.task.labels.complete',
              )}
            </PopupMenuButton>
          </PopupMenuListItem>
        </PopupMenuList>
      )}

      {completeActions}

      {(!isHabit || !!occurrenceDate) && (
        <PopupMenuList hasBottomBorder>
          {!isHabit && (
            <>
              <TaskDateQuickActions
                tasks={quickActionTasks}
                onSelectDate={selectDate}
                onClose={onClose}
                onAction={onAction}
                onUpdate={onUpdate}
              />
              <TaskPriorityQuickActions
                tasks={quickActionTasks}
                onClose={onClose}
                onAction={onAction}
                onUpdate={onUpdate}
              />
            </>
          )}
          {isHabit && !!occurrenceDate && (
            <TaskRescheduleQuickActions
              task={task}
              occurrenceDate={occurrenceDate}
              onSelectDate={selectDate}
              onClose={onClose}
              onAction={onAction}
              onUpdate={onUpdate}
            />
          )}
        </PopupMenuList>
      )}

      <PopupMenuList hasBottomBorder>
        {!isFocusBlock && (
          <PopupMenuListItem>
            <PopupMenuButton
              onClick={updateLinkedItem}
              start={<Icon icon={Target} />}
              shortcut={['G']}
            >
              {t('context-menus.task.multi-select.options.linked-item.label')}
            </PopupMenuButton>
          </PopupMenuListItem>
        )}
        {isHabit && (
          <PopupMenuListItem>
            <PopupMenuButton
              onClick={updatePriority}
              start={<Icon icon={Warning} />}
              shortcut={['P']}
            >
              {t('context-menus.task.multi-select.options.priority.label')}
            </PopupMenuButton>
          </PopupMenuListItem>
        )}
        {!isHabit && !isFocusBlock && (
          <PopupMenuListItem>
            <PopupMenuButton
              onClick={updateDeadlineDeadline}
              start={<Icon icon={Flag} />}
              shortcut={getPlatformShortcut(['Shift', 'D'])}
            >
              {t(
                'context-menus.task.multi-select.options.deadline-deadline.label',
              )}
            </PopupMenuButton>
          </PopupMenuListItem>
        )}
        {!isFocusBlock && (
          <PopupMenuListItem>
            <PopupMenuButton
              onClick={updateTags}
              start={<Icon icon={Tag} />}
              shortcut={['T']}
            >
              {t('context-menus.task.multi-select.options.tags.label')}
            </PopupMenuButton>
          </PopupMenuListItem>
        )}
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={updateReminderTime}
            start={<Icon icon={BellNotifications} />}
            shortcut={['R']}
          >
            {t('context-menus.task.multi-select.options.reminder-time.label')}
          </PopupMenuButton>
        </PopupMenuListItem>
      </PopupMenuList>
    </>
  );
};
